import React, { useState, useRef, useEffect } from "react"
import { AspectRatio, Box, Container, Grid, Heading, Stack, Text, Button } from "@chakra-ui/react"

import { Location } from "@root/types/global"
import Breadcrumb from "@components/Breadcrumb"
import CollectionFeaturedList from "./CollectionFeaturedList"
import { useImage } from "@app/hooks/useImage"
import Image from "@app/components/Image/Image"
import { useMedia } from "@app/hooks/useMedia"

export interface CollectionBannerProps {
  collection: any
  description: any
  location: Location
}

const CollectionBanner = ({ collection, description, location }: CollectionBannerProps) => {
  const { getGatsbyImage } = useImage()
  const bannerImage = getGatsbyImage(collection?.bannerImage, collection?.bannerImage?.alt)
  const { isBase } = useMedia()
  const [isExpanded, setIsExpanded] = useState(false)
  const [isTruncated, setIsTruncated] = useState(false)
  const textRef = useRef(null)

  useEffect(() => {
    if (textRef.current) {
      setIsTruncated(textRef.current.scrollHeight > textRef.current.clientHeight)
    }
  }, [])

  const toggleDescription = () => {
    setIsExpanded(!isExpanded)
  }

  const renderDescription = () => {
    if (!isBase) {
      return <Box>{description}</Box>
    }

    return (
      <Box>
        <Box
          ref={textRef}
          overflow="hidden"
          maxHeight={isExpanded ? "none" : "calc(3em + 12px)"}
          position="relative"
          sx={{
            display: "-webkit-box",
            WebkitLineClamp: isExpanded ? "unset" : 2,
            WebkitBoxOrient: "vertical",
            maxWidth: { base: "100%", sm: "70%" },
            margin: "0 auto",
          }}
        >
          {description}
        </Box>
        {isTruncated && (
          <Button
            onClick={toggleDescription}
            variant="link"
            size="sm"
            color="black"
            fontWeight="normal"
            padding={0}
            height="auto"
            minWidth={0}
            textDecoration="underline"
            _hover={{ textDecoration: "underline" }}
          >
            {isExpanded ? "show less" : "read more"}
          </Button>
        )}
      </Box>
    )
  }

  return (
    <>
      {collection.enabledFeaturedCollections ? (
        <CollectionFeaturedList 
          collection={collection} 
          location={location}  
        />
      ) : collection.bannerOptions === "specialBanner" ? (
        <Grid templateColumns={["none", "repeat(2, 1fr)"]}>
          <Box bg={collection?.bgColour?.hex || "brand.turquoise"} px={isBase ? 1.5 : 4}>
            <Stack py={isBase ? 1.5 : 3} spacing={2} alignItems="center">
              <Breadcrumb data={collection} location={location} theme={collection?.textColor === "white" ? "light" : "dark"} style={{ textAlign: "center" }} />
              <Heading as="h1" size="h1" color={collection?.textColor || "white"} mt="0 !important" textAlign="center">
                {collection.title}
              </Heading>
              <Text
                size="sm"
                textAlign="center"
                color={`${collection?.textColor}` || "white"}
                mt="0 !important"
                sx={{
                  maxWidth: { base: "100%", sm: "70%" },
                  margin: "0 auto",
                  "*": { color: `${collection?.textColor || "white"}!important` },
                }}
              >
                {description && renderDescription()}
              </Text>
            </Stack>
          </Box>
          <AspectRatio minH={isBase ? undefined : "250px"} ratio={isBase ? 2 / 1 : 3.28 / 1}>
            {bannerImage ? <Image {...bannerImage} objectFit="cover" /> : <Image {...collection.image} objectFit="cover" />}
          </AspectRatio>
        </Grid>
      ) : (
        <Stack>
          <Box bg={"#F3F2EE"} h={["auto"]}>
            <Container>
              <Stack py={isBase ? 1.5 : 3} spacing={2} alignItems="center">
                <Breadcrumb data={collection} location={location} theme="dark" />
                <Heading as="h1" size="h1" mt="0 !important" textAlign="center">
                  {collection.title}
                </Heading>
                <Text size="sm" textAlign="center" mt="0 !important" sx={{ maxWidth: { base: "100%", sm: "70%" }, margin: "0 auto" }}>
                  {description && renderDescription()}
                </Text>
              </Stack>
            </Container>
          </Box>
        </Stack>
      )}
    </>
  )
}

export default CollectionBanner
